import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { FaUserMd, FaDna, FaProcedures, FaVenusMars, FaMale, FaVial, FaUserFriends, FaRegClock, FaChartLine, FaHeart, FaStethoscope, FaHospital, FaHandshake, FaLock } from "react-icons/fa";
import Section from "../Section";
import FaqSectionStyle2 from "../Section/FaqSection/FaqSectionStyle2";
import { pageTitle } from "../../helpers/PageTitle";
import BannerSectionStyle3 from "../Section/BannerSection/BannerSectionStyle3";
import styled from "styled-components";
import FaqSectionStyle4 from "../FaqSection/FaqSectionStyle4";
import WorkingProcess from "../Section/WorkingProcess";
import Spacing from "../Spacing";
import Post from "../Post";
import { Helmet } from "react-helmet";

const faqData = [
  {
    title: "What are TESA and PESA, and how do they differ ?",
    content:
      "TESA (Testicular Sperm Aspiration) and PESA (Percutaneous Epididymal Sperm Aspiration) are procedures used to retrieve sperm directly from the male reproductive organs in cases where sperm are not present in the ejaculate (azoospermia). TESA involves using a needle to extract sperm directly from the testicular tissue, while PESA involves using a needle to extract sperm from the epididymis, a duct behind the testis where sperm mature and are stored.",
  },
  {
    title: "Who can benefit from TESA/PESA procedures ?",
    content:
      "TESA and PESA are beneficial for men with obstructive azoospermia, which involves blockages in the reproductive tract preventing sperm from being ejaculated, often due to vasectomy, congenital absence of the vas deferens, or infections; non-obstructive azoospermia, which involves sperm production issues within the testes; failed vasectomy reversals, when surgical attempts to reverse vasectomy are unsuccessful; and other conditions where sperm retrieval from the testes or epididymis is necessary for assisted reproductive techniques such as IVF or ICSI.",
  },
  {
    title: "How are TESA and PESA procedures performed ?",
    content:
      "Both procedures are minimally invasive and typically performed under local anesthesia. TESA involves inserting a fine needle into the testicle to aspirate small amounts of tissue, which is then processed in the lab to extract viable sperm. PESA involves inserting a fine needle into the epididymis to aspirate fluid containing sperm, which is then processed to extract viable sperm. Both procedures are usually done on an outpatient basis and take about 20-30 minutes.",
  },
  {
    title: "What are the success rates and outcomes of TESA/PESA ?",
    content:
      "The success rates of TESA and PESA in retrieving viable sperm can vary based on the underlying cause of azoospermia. For obstructive azoospermia, success rates are often above 90% as sperm production is typically normal. For non-obstructive azoospermia, success rates generally range between 30-70% depending on the extent of sperm production issues. Retrieved sperm can be used immediately for IVF/ICSI or cryopreserved for future use. Success rates of resulting IVF/ICSI cycles depend on several factors, including the quality of the retrieved sperm and the female partner’s age and health.",
  },
  {
    title: "Are there any risks or side effects associated with TESA/PESA ?",
    content:
      "TESA and PESA are generally safe procedures with minimal risks, but potential side effects and risks include discomfort or pain at the site of needle insertion, which can be managed with over-the-counter pain medication; minor swelling or bruising of the scrotum, usually resolving within a few days; a rare risk of infection at the needle insertion site, which can be mitigated with proper aseptic techniques; minimal risk of bleeding within the scrotum, generally self-limiting and not requiring intervention; and the emotional and psychological aspects of undergoing these procedures and potential outcomes, which should be considered and supported with counseling if needed.",
  },
];

const workingProcessData = [
  {
    title: "Initial Consultation and Assessment",
    subTitle:
      "A comprehensive evaluation of the male partner is conducted, including a review of medical history, a physical examination, and fertility testing. This is followed by a detailed discussion of the TESA procedure, covering potential risks, success rates, and expected outcomes.",
    iconUrl: "/images/home_2/department_icon1.png",
    number: "01",
  },
  {
    title: "Sperm Aspiration",
    subTitle:
      "TESA involves using a fine needle to aspirate sperm directly from the testicle under local anesthesia or sedation, with the procedure guided by ultrasound to locate and access the testicular tissue containing sperm.",
    iconUrl: "/images/home_2/departmentLap2.png",
    number: "02",
  },
  {
    title: "Sperm Retrieval and Processing",
    subTitle:
      "The aspirated tissue is processed in the laboratory to isolate viable sperm for use in assisted reproductive techniques like IVF with ICSI.",
    iconUrl: "/images/home_2/departmentLap4.png",
    number: "03",
  },
];

const PesaData = [
  {
    title: "Initial Consultation and Assessment",
    subTitle:
      "A comprehensive evaluation of the male partner is conducted, including a review of medical history, a physical examination, and fertility testing. This is followed by a detailed discussion of the PESA procedure, covering potential risks, success rates, and expected outcomes.",
    iconUrl: "/images/home_2/department_icon1.png",
    number: "01",
  },
  {
    title: "Sperm Aspiration",
    subTitle:
      "PESA involves using a fine needle to aspirate sperm directly from the epididymis, a small organ located near the testicle, under local anesthesia or sedation. The procedure is guided by ultrasound to locate and access the epididymal tissue containing sperm.",
    iconUrl: "/images/home_2/departmentLap2.png",
    number: "02",
  },
  {
    title: "Sperm Retrieval and Processing",
    subTitle:
      "The aspirated fluid is processed in the laboratory to isolate viable sperm for use in assisted reproductive techniques like IVF with ICSI.",
    iconUrl: "/images/home_2/deprtmentLap5.png",
    number: "03",
  },
];

const relatedBlog = [
  {
    title:
      "The Importance of Mental Health: Understanding and Managing Anxiety Disorders",
    thumbUrl: "/images/blog/post_1.jpeg",
    date: "March 12",
    btnText: "Learn More",
    href: "/blog/blog-details",
  },
];

export default function TesaPesa() {
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('cs_active');
        }
      });
    }, observerOptions);

    document.querySelectorAll('.cs_reveal').forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Helmet>
        <title>TESA/PESA – Sperm Retrieval Techniques for Male Infertility</title>
        <meta name="description" content="Minimally invasive surgical sperm retrieval methods like TESA and PESA for male infertility diagnosis and treatment." />
        <meta name="keywords" content="TESA, PESA, sperm retrieval, male fertility treatment, azoospermia solution" />
        <link rel="canonical" href="https://jabalpurivfcentre.com/Tesa-Pesa" />
      </Helmet>
      <Container className="cs_iui_new_wrap">
        <BannerSectionStyle3
          bgUrl="/images/Tesa-Pesa.jpeg"
          title="Welcome to <br /> Pluro Jabalpur<br /> Fertility Center"
          subTitle="TESA/PESA (Testicular Sperm Aspiration / Percutaneous Epididymal Sperm Aspiration)"
        />

        {/* Intro Section */ }
        <Section topMd={ 160 } topLg={ 120 } topXl={ 80 }>
          <div className="container">
            <div className="row align-items-center cs_reveal cs_premium_intro_row">
              <div className="col-lg-5">
                <div className="cs_intro_left">
                  <span className="cs_intro_subtitle">Pluro Jabalpur Fertility Centre</span>
                  <h1 className="cs_intro_title">
                    Advanced Male Infertility Treatments
                  </h1>
                  <div className="cs_intro_divider"></div>
                </div>
              </div>
              <div className="col-lg-7">
                <div className="cs_intro_right">
                  <p>
                    TESA (Testicular Sperm Aspiration) and PESA (Percutaneous Epididymal Sperm Aspiration) are specialized procedures designed to retrieve sperm directly from the testicles or epididymis of men who have fertility issues.
                  </p>
                  <p className="mb-0">
                    At <strong>Pluro Jabalpur Fertility Centre</strong>, known as the Best Fertility Clinic in Jabalpur, we offer advanced male infertility treatments under the expert guidance of Dr. Archana Shrivastava, the Best IVF Doctor in Jabalpur. Our mission is to provide personalized, compassionate care and effective solutions for couples facing male infertility challenges.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* Luxury TESA/PESA Section */ }
        <div className="cs_luxury_section" id="tesa-pesa" style={ { paddingTop: '80px' } }>
          <div className="organic-bg"></div>
          <div className="container relative z-10">
            <div className="cs_luxury_text_box cs_reveal">
              <h3 style={{ color: '#502A6F' }}>What is TESA/PESA ?</h3>
              <p>TESA and PESA are minimally invasive procedures used to collect sperm when there is a blockage or absence of sperm in the ejaculate. These techniques enable men with certain types of infertility to still father biological children through assisted reproductive technologies like IVF and ICSI.</p>
            </div>

            <div className="text-center mb-5 cs_reveal">
              <h2 className="cs_fs_40 cs_medium" style={ { fontFamily: '"Playfair Display", serif', fontStyle: 'italic', marginTop: '60px', color: '#1c2b38' } }>When is TESA/PESA Recommended ?</h2>
              <p style={ { fontSize: '18px', color: '#586776', marginBottom: '40px' } }>TESA and PESA may be recommended for men with the following conditions :</p>
            </div>

            <div className="cs_luxury_tight_grid">
              <div className="cs_premium_service_card theme_orange cs_reveal">
                <span className="cs_psc_number">01.</span>
                <h4>Obstructive Azoospermia</h4>
                <p>Blockage preventing sperm from being ejaculated.</p>
              </div>

              <div className="cs_premium_service_card theme_orange cs_reveal">
                <span className="cs_psc_number">02.</span>
                <h4>Non-obstructive Azoospermia</h4>
                <p>Sperm production issues within the testicles.</p>
              </div>

              <div className="cs_premium_service_card theme_orange cs_reveal">
                <span className="cs_psc_number">03.</span>
                <h4>Previous Vasectomy</h4>
                <p>Men who have undergone a vasectomy and wish to father children again.</p>
              </div>

              <div className="cs_premium_service_card theme_orange cs_reveal">
                <span className="cs_psc_number">04.</span>
                <h4>Failed Sperm Retrieval</h4>
                <p>Unsuccessful sperm retrieval using other methods like microsurgical techniques.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Working Process Sections */ }
        <Section topMd={ 80 } topLg={ 60 } topXl={ 40 }>
          <WorkingProcess
            sectionTitle="The TESA Procedure"
            sectionTitleUp=""
            sectionTitleDown=""
            sectionSubTitle=""
            data={ workingProcessData }
          />
        </Section>
        <Section topMd={ 80 } topLg={ 60 } topXl={ 40 }>
          <WorkingProcess
            sectionTitle="The PESA Procedure"
            sectionTitleUp=""
            sectionTitleDown=""
            sectionSubTitle=""
            data={ PesaData }
          />
        </Section>

        {/* Benefits Section */ }
        <Section topMd={ 120 } topLg={ 100 } topXl={ 80 }>
          <div className="container cs_reveal">
            <h2 className="text-center cs_fs_40 cs_medium mb-5">Benefits of TESA/PESA</h2>
            <div className="row cs_gap_y_40 justify-content-center">
              <div className="col-lg-4 col-md-6">
                <div className="cs_benefit_card_luxury theme_orange h-100">
                  <div className="cs_bcl_icon_wrap">
                    <FaVial />
                  </div>
                  <h3>Effective Sperm Retrieval</h3>
                  <p>Allows men with certain types of male infertility to obtain viable sperm for use in assisted reproductive technologies</p>
                </div>
              </div>
              <div className="col-lg-4 col-md-6">
                <div className="cs_benefit_card_luxury theme_purple h-100">
                  <div className="cs_bcl_icon_wrap">
                    <FaMale />
                  </div>
                  <h3>Option for Biological Fatherhood</h3>
                  <p>Provides an opportunity for men who may otherwise be unable to conceive naturally to father biological children.</p>
                </div>
              </div>
              <div className="col-lg-4 col-md-6">
                <div className="cs_benefit_card_luxury theme_orange h-100">
                  <div className="cs_bcl_icon_wrap">
                    <FaProcedures />
                  </div>
                  <h3>Minimally Invasive</h3>
                  <p>Both TESA and PESA are outpatient procedures that typically involve minimal discomfort and recovery time.</p>
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* Why Choose Us Section */ }
        <Section topMd={ 120 } topLg={ 100 } topXl={ 80 } bottomMd={ 80 } bottomLg={ 60 } bottomXl={ 40 }>
          <div className="container cs_reveal">
            <div className="cs_premium_why_choose">
              <div className="cs_premium_section_header">
                <h2>Why Choose Jabalpur Fertility Centre for TESA/PESA ?</h2>
              </div>

              <div className="row cs_gap_y_40 justify-content-center" style={ { display: 'flex', flexWrap: 'wrap' } }>
                <div className="col-lg-4 col-md-6">
                  <div className="cs_why_card_premium h-100">
                    <div className="cs_wcp_icon">
                      <FaUserMd style={ { fontSize: '32px', color: '#ff790f' } } />
                    </div>
                    <h3>Expertise and Experience</h3>
                    <p>Led by Dr. Archana Shrivastava, our team has extensive experience in managing successful TESA and PESA procedures.</p>
                  </div>
                </div>
                <div className="col-lg-4 col-md-6">
                  <div className="cs_why_card_premium h-100">
                    <div className="cs_wcp_icon">
                      <FaHandshake style={ { fontSize: '32px', color: '#ff790f' } } />
                    </div>
                    <h3>Personalized Care</h3>
                    <p>We tailor our donor programs to meet the unique needs of each couple or individual, ensuring the best possible outcomes.</p>
                  </div>
                </div>
                <div className="col-lg-4 col-md-6">
                  <div className="cs_why_card_premium h-100">
                    <div className="cs_wcp_icon">
                      <FaStethoscope style={ { fontSize: '32px', color: '#ff790f' } } />
                    </div>
                    <h3>Comprehensive Fertility Services</h3>
                    <p>From initial consultation and complete fertility workup to advanced treatments like IVF, ICSI, and donor programs, we offer a full spectrum of fertility services.</p>
                  </div>
                </div>
                <div className="col-lg-4 col-md-6">
                  <div className="cs_why_card_premium h-100">
                    <div className="cs_wcp_icon">
                      <FaHospital style={ { fontSize: '32px', color: '#ff790f' } } />
                    </div>
                    <h3>State-of-the-Art Facility</h3>
                    <p>Our clinic is equipped with the latest technology and facilities to provide top-notch fertility care.</p>
                  </div>
                </div>
                <div className="col-lg-4 col-md-6">
                  <div className="cs_why_card_premium h-100">
                    <div className="cs_wcp_icon">
                      <FaHeart style={ { fontSize: '32px', color: '#ff790f' } } />
                    </div>
                    <h3>Patient-Centered Approach</h3>
                    <p>We prioritize your comfort and well-being, offering compassionate support throughout your fertility journey.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* FAQ Section */ }
        <Section topMd={ 40 } topLg={ 20 } topXl={ 0 }>
          <div className="cs_iui_faq_wrapper cs_reveal">
            <FaqSectionStyle4
            sectionTitle="Frequently Asked <br />Questions"
            data={ faqData }
            faqImgUrl="/images/home_4/faq_img.jpg"
            spiningImgUrl="/images/home_1/about_mini.svg"
          />
          </div>
        </Section>

        {/* Contact Us Section */ }
        <Section topMd={ 60 } topLg={ 40 } topXl={ 20 } bottomMd={ 100 } bottomLg={ 80 } bottomXl={ 60 }>
          <div className="container cs_reveal">
            <div className="cs_ivf_contact_banner" style={ { background: '#c77842', borderRadius: '20px', padding: '60px 50px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '30px' } }>
              <div className="cs_icc_content" style={ { flex: '1', minWidth: '300px' } }>
                <h2 style={ { color: '#fff', fontSize: '36px', marginBottom: '15px', fontWeight: '700' } }>Contact Us</h2>
                <p style={ { color: 'rgba(255,255,255,0.9)', fontSize: '18px', margin: '0', lineHeight: '1.6' } }>
                  If you are considering TESA or PESA as part of your fertility treatment, contact Jabalpur Fertility Centre today. Our expert team is here to guide you through the process and help you achieve your dream of starting a family.
                </p>
              </div>
              <div className="cs_icc_action">
                <Link to="/contact" className="cs_btn" style={ { background: '#fff', color: '#c77842', padding: '15px 40px', borderRadius: '50px', fontWeight: '600', fontSize: '18px', display: 'inline-flex', alignItems: 'center', textDecoration: 'none', boxShadow: '0 10px 20px rgba(0,0,0,0.1)' } }>
                  <span>Get in Touch</span>
                </Link>
              </div>
            </div>
          </div>
        </Section>

        <Section bottomMd={ 200 } bottomLg={ 150 } bottomXl={ 110 }>
          <div className="container cs_reveal">
            <h2 className="mb-0 cs_fs_40 cs_medium">Related Articles</h2>
            <Spacing md="57" />
            <div className="row cs_gap_y_40">
              { relatedBlog?.map((item, index) => (
                <div className="col-xl-12" key={ index }>
                  <Post { ...item } />
                </div>
              )) }
            </div>
          </div>
        </Section>
      </Container>
    </>
  );
}

const Container = styled.div`
  .margin {
    margin-left: 25px;
  }
  .heading{
    font-size: 32px;
     @media (max-width: 991px) {
      font-size: 28px;
    }
  }
`;
